/**
 * Fuel-load vegetation classification — shared by FuelLoad3DTrees (which plants
 * the trees) and MapDimFilm (which shades the basemap under exactly those
 * stands). Kept in its own module so both can import it without dragging a React
 * component into the dependency (and so fast-refresh keeps working on the trees).
 *
 * The MVT carries no model-class id, so the archetype is inferred from which
 * `fuelCharacteristics` keys are present. Source key strings are verified
 * against fire_spread_new/fuel_models/*.py.
 */

/** The fuel-load tiles' source layer. */
export const SOURCE_LAYER = "fuel_load";
/** Stands with canopy height below this get no trees — sub-metre "canopy" is shrub/regrowth noise. */
export const MIN_CANOPY_HEIGHT_M = 1;

/** FBP System stand heights (m) by fuel-type code; absent for grass, slash and non-fuel. */
const FBP_STAND_HEIGHT_M: Record<number, number> = {
  1: 10, 2: 7, 3: 18, 4: 10, 5: 25, 6: 14, 7: 20,
  8: 13, 9: 13, 22: 13,
  10: 13, 11: 13, 21: 13, 12: 8, 13: 8, 24: 8,
};
/** Readability gain on the bare-trunk fraction (CBH/CH). */
export const CBH_VISUAL_GAIN = 1;

export function clamp(v: number, a: number, b: number) {
  return Math.min(b, Math.max(a, v));
}

/**
 * Real LANDFIRE forest CBD almost entirely lives in 0.03–0.25 kg/m³; an
 * earlier 0.02–0.40 span left everything mid-ramp and indistinguishable.
 */
const cbdNorm = (cbd: number) => clamp((cbd - 0.03) / 0.22, 0, 1);

/**
 * Pine plantation canopy bulk density normaliser. Bulk density runs 0 → 0.15
 * kg/m³: a fully thinned/cleared stand (CBD 0) has no stems, a dense mature one
 * (CBD 0.15) is tightly packed. Shared by the fullness (crown density) and
 * spacing (stem density) proxies so both read the same notion of stand density.
 */
const pineBulkNorm = (bd: number) => clamp(bd / 0.15, 0, 1);

export function parseCharacteristics(
  value: unknown
): Record<string, number> | null {
  if (value === undefined || value === null) return null;
  let raw: unknown = value;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
  const out: Record<string, number> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([k, v]) => {
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isNaN(n)) out[k] = n;
  });
  return out;
}

// ---------------------------------------------------------------------------
// Vegetation classification — infer the archetype from which characteristic
// keys are present. Returns the geometry inputs for one stand, or null when the
// cell should render bare (grass and grassland are intentionally not rendered).
// ---------------------------------------------------------------------------
export type VegKind = "eucalypt" | "pine" | "sb" | "shrub";

export interface StandSpec {
  kind: VegKind;
  height: number; // nominal plant height (m)
  baseFrac: number; // bare-trunk fraction of height (0 for shrub/grass)
  fullness: number; // 0..1 crown density / greenness proxy
  understory: number; // understory shrub height (m), eucalypt only
  spacingM: number; // planting pitch before zoom/budget scaling
}

export function classifyStand(
  chars: Record<string, number> | null
): StandSpec | null {
  if (chars) {
    // Tree / Shrub polygons from the fine-detail editor: their canopy is drawn
    // from the project's own crowns (useCrowns → PlantRequest.crowns), one per
    // tree where the imagery has it, so no lattice stand goes over them.
    if (chars.explicit_crowns) return null;
    const standH = chars.stand_height_m ?? 0;
    // Eucalypt (Vesta) — understory_height_m is unique to this model.
    if ("understory_height_m" in chars && standH >= MIN_CANOPY_HEIGHT_M) {
      // Height comes straight from the data, clamped to a realistic mature
      // gum (~40 m) so bad pixels never draw redwoods.
      return {
        kind: "eucalypt",
        height: Math.min(standH, 40),
        baseFrac: 0.55, // eucalypts carry a high, open crown
        fullness: 0.45,
        understory: chars.understory_height_m ?? 0,
        spacingM: 11,
      };
    }
    // Pine plantation (Pine) — bulk_density is unique to this model.
    if ("bulk_density" in chars && standH >= MIN_CANOPY_HEIGHT_M) {
      const bdN = pineBulkNorm(chars.bulk_density);
      // CBD driven to (near) 0 means the canopy is cleared — plant no stems.
      if (bdN <= 0) return null;
      // Data height, clamped to a realistic mature plantation pine (~30 m).
      const h = Math.min(standH, 30);
      const base = chars.canopy_base_height_m ?? h * 0.3;
      return {
        kind: "pine",
        height: h,
        baseFrac: clamp(base / h, 0, 0.85),
        fullness: bdN * 0.5 + 0.5,
        understory: 0,
        // Stem density tracks bulk density: spacing ∝ 1/√density, so a dense
        // mature stand (bd 0.15) is tightly packed (~4 m) and stems thin out
        // sharply toward the cleared end before dropping to none at CBD 0.
        spacingM: clamp(4 / Math.sqrt(bdN), 4, 30),
      };
    }
    // Southern pine — suffix-less stand_height; base is 85% so a small top tuft.
    if ("stand_height" in chars && chars.stand_height >= MIN_CANOPY_HEIGHT_M) {
      const h = Math.min(chars.stand_height, 30);
      const base = chars.canopy_base_height ?? h * 0.85;
      // Stem density comes straight from the model's stems/ha. Grid pitch is the
      // mean stem spacing: spacingM = √(10000 / stems_per_ha). Thinning lowers
      // stems_per_ha, which widens the pitch; a cleared stand plants nothing.
      const stems = chars.stems_per_ha ?? 1000;
      if (stems <= 0) return null;
      return {
        kind: "pine",
        height: h,
        baseFrac: clamp(base / h, 0, 0.9),
        fullness: 0.5,
        understory: 0,
        spacingM: clamp(Math.sqrt(10000 / stems), 3, 30),
      };
    }
    // Canadian FBP fuel types — stand height from the FBP System's stand
    // descriptions (ST-X-3 Table 8), crown base from the fuel model's CBH.
    if ("fbp_fuel_type" in chars) {
      const code = Math.round(chars.fbp_fuel_type);
      const standHeight = FBP_STAND_HEIGHT_M[code];
      if (standHeight == null) {
        // Slash beds are a low woody layer; grass, non-fuel and water carry nothing.
        return code >= 16 && code <= 18
          ? { kind: "shrub", height: 1.5, baseFrac: 0, fullness: 0.5, understory: 0, spacingM: 3.5 }
          : null;
      }
      const base = clamp(chars.fbp_cbh_m ?? standHeight * 0.3, 0, standHeight * 0.9);
      const deciduous = code === 8 || code === 9 || code === 22;
      return {
        kind: deciduous ? "sb" : "pine",
        height: standHeight,
        baseFrac: clamp(base / standHeight, 0.03, 0.78),
        fullness: 0.5,
        understory: 0,
        spacingM: 5,
      };
    }
    // Scott & Burgan forest — LANDFIRE canopy height present.
    if ("canopy_height_m" in chars && chars.canopy_height_m >= MIN_CANOPY_HEIGHT_M) {
      // LANDFIRE canopy height, clamped to a tall temperate stand (~45 m).
      const h = Math.min(chars.canopy_height_m, 45);
      const cbd = chars.canopy_bulk_density ?? 0.08;
      const base = clamp(chars.canopy_base_height_m ?? h * 0.3, 0, h * 0.9);
      return {
        kind: "sb",
        height: h,
        baseFrac: clamp((base / h) * CBH_VISUAL_GAIN, 0.03, 0.78),
        fullness: cbdNorm(cbd),
        understory: 0,
        spacingM: 17 - 10 * cbdNorm(cbd),
      };
    }
    // Heathland — elevated woody layer, capped ~4 m, no real trunk.
    if ("elevated_height" in chars && chars.elevated_height > 0.25) {
      return {
        kind: "shrub",
        height: Math.min(chars.elevated_height, 4),
        baseFrac: 0,
        fullness: 0.6,
        understory: 0,
        spacingM: 3.5,
      };
    }
    // Scott & Burgan grass (GR1–GR9): live herbaceous fuel and NO live woody
    // (GS/SH carry live woody; TL/SB carry neither). These are grass, so hand
    // them to the ground-cover grass pass instead of the woody shrub-bed rule
    // below — otherwise the deep GR7–GR9 beds (≥0.5 m) render as shrubs.
    if (
      "live_herb_load_t_ha" in chars &&
      chars.live_herb_load_t_ha > 0 &&
      (chars.live_woody_load_t_ha ?? 0) <= 0.01
    ) {
      return null;
    }
    // Scott & Burgan without canopy layers — render only woody fuel beds
    // (depth >= 0.5 m) as shrub; shallower beds are grass and are skipped.
    if ("depth_m" in chars && chars.depth_m >= 0.5) {
      return {
        kind: "shrub",
        height: Math.min(chars.depth_m * 1.5, 3),
        baseFrac: 0,
        fullness: 0.5,
        understory: 0,
        spacingM: 3,
      };
    }
  }
  // Grass / grassland (incl. the Simple model) is intentionally not rendered.
  return null;
}

// ---------------------------------------------------------------------------
// Ground-cover classification — the surface/near-surface layer that sits below
// the canopy. Unlike the tree archetype (inferred from canopy characteristics),
// this is driven by the per-pixel FUEL LOADS the sim emits into the MVT's
// `strataBreakdown`: `surface` (t/ha) is the litter / fine dead bed, `elevated`
// (t/ha) is the near-surface shrub/understory load. The denser the surface
// fuel, the more leaf litter is scattered; where there's an elevated load, low
// shrubs appear, and tree stands with a heavy surface load drop fallen logs.
// ---------------------------------------------------------------------------

/** Surface load (t/ha) that reads as a near-continuous litter carpet. */
const LITTER_FULL_T_HA = 14;
/** Elevated load (t/ha) at which the near-surface shrub layer saturates. */
const ELEVATED_FULL_T_HA = 6;
/**
 * Surface load (t/ha) at which grass cover saturates — at/above this the grass
 * is at max height + density; ~2.5 t/ha (gN 0.5) already reads as a high load.
 */
const GRASS_FULL_T_HA = 5;
/** Fixed planting pitch (m) for the ground layer — finer than the tree grid. */
const GROUND_SPACING_M = 1.8;

/** Tree archetype, plus "grass" for cells that carry only a grassy surface fuel. */
export type GroundKind = VegKind | "grass";

export interface GroundCoverSpec {
  /** Per-ground-cell probability a litter patch is placed (0..1). */
  litterProb: number;
  /** 0..1 surface-fuel proxy → litter patch size + colour darkness. */
  litterFullness: number;
  /** Per-ground-cell probability a near-surface shrub is placed (0..1). */
  shrubProb: number;
  /** 0..1 elevated-fuel proxy → shrub greenness/size. */
  shrubFullness: number;
  /** Near-surface shrub height (m), kept below the canopy. */
  shrubHeight: number;
  /** Per-ground-cell probability a fallen log is placed (0..1, tree stands only). */
  logProb: number;
  /** Per-ground-cell probability grass is placed (0..1). */
  grassProb: number;
  /** Tufts placed per grass cell when present — drives sward density by load. */
  grassClumps: number;
  /** 0..1 surface-fuel proxy → grass colour (green → cured straw). */
  grassFullness: number;
  /** Grass tuft height (m) — grows steeply with the surface load. */
  grassHeight: number;
  /** Ground-cell pitch (m) before zoom/budget scaling. */
  spacingM: number;
}

/**
 * Derive the ground-cover density for one cell from its fuel loads. `strata`
 * is the parsed `strataBreakdown` (t/ha per stratum); `chars` supplies a
 * near-surface height where the model emits one. `kind` is the cell's tree
 * archetype, or "grass" for a grassy surface-only cell (logs only fall under
 * woody stands; grass is dense in grassland and a lighter understory in open
 * eucalypt / Scott & Burgan forest). Returns null when there is no meaningful
 * surface or near-surface fuel to render.
 */
export function classifyGroundCover(
  chars: Record<string, number> | null,
  strata: Record<string, number> | null,
  kind: GroundKind,
  fuelLoad: number | null
): GroundCoverSpec | null {
  // Surface load drives the litter / grass. When the strata split is missing,
  // fall back to the bulk of the total load (surface dominates non-canopy fuel).
  let surface = strata?.surface;
  if (surface == null || !Number.isFinite(surface)) {
    surface = fuelLoad != null && Number.isFinite(fuelLoad) ? fuelLoad * 0.6 : 0;
  }
  const elevated = strata?.elevated ?? 0;
  if (surface <= 0.2 && elevated <= 0.2) return null;

  const isGrass = kind === "grass";
  const woody = kind === "eucalypt" || kind === "pine" || kind === "sb";

  const sN = clamp(surface / LITTER_FULL_T_HA, 0, 1);
  const eN = clamp(elevated / ELEVATED_FULL_T_HA, 0, 1);
  const gN = clamp(surface / GRASS_FULL_T_HA, 0, 1);

  // Near-surface shrub height from whichever low-layer characteristic the model
  // emits, with a raised default + cap so the shrubs read as a substantial
  // near-surface layer (still below the canopy).
  const lowH =
    chars?.understory_height_m ??
    chars?.elevated_height ??
    (chars?.depth_m != null ? chars.depth_m * 1.4 : undefined);
  const shrubHeight = clamp(lowH ?? 0.7, 0.35, 2.2);

  // Shrubs scale with the elevated load. Heath/shrub stands ARE a near-surface
  // layer, so they carry a denser floor even when the strata split is coarse.
  let shrubProb = clamp(0.04 + 0.5 * eN, 0, 0.55);
  if (kind === "shrub") shrubProb = Math.max(shrubProb, 0.18 + 0.4 * sN);
  // Under explicit crowns the shrubs are the crowns themselves — no blobs.
  if (chars?.explicit_crowns) shrubProb = 0;

  // Fallen logs are coarse woody debris: only under tree stands, only where the
  // surface (dead + fine) load is high, and rare even at the top of the range.
  const logProb = woody ? clamp((sN - 0.45) * 0.4, 0, 0.085) : 0;

  // Grass: dense in grasslands, a grassy understory in open eucalypt / SB
  // forest, none under pine needle litter or woody heath/shrub. In grasslands
  // the per-cell probability is high and density comes from multiple tufts per
  // cell (grassClumps), which grows steeply with the surface load.
  let grassProb = 0;
  let grassClumps = 1;
  if (isGrass) {
    grassProb = clamp(0.6 + 0.4 * gN, 0, 1);
    grassClumps = 1 + Math.round(gN * 5); // 1 (sparse) → 6 tufts/cell (max load)
  } else if (kind === "eucalypt") {
    grassProb = clamp(0.1 + 0.35 * gN, 0, 0.45);
  } else if (kind === "sb") {
    grassProb = clamp(0.06 + 0.22 * gN, 0, 0.3);
  }

  return {
    // Grasslands carry grass thatch, not leaf litter, so keep litter light there.
    // Scattered at 25% density (down 75% from the original).
    litterProb: isGrass
      ? clamp((0.05 + 0.2 * sN) * 0.25, 0, 0.3)
      : clamp((0.12 + 0.9 * sN) * 0.25, 0, 1),
    litterFullness: sN,
    shrubProb,
    shrubFullness: eN,
    shrubHeight,
    logProb,
    grassProb,
    grassClumps,
    grassFullness: gN,
    // S&B grass beds carry their fuel-bed depth; render the grass much taller
    // than the raw bed depth (×2.5) so it reads clearly — GR1 still short, the
    // deep GR7–GR9 beds become tall tussock. Models with no depth (e.g. the
    // Australian grassland models) scale by surface load.
    grassHeight:
      chars?.depth_m != null && chars.depth_m > 0
        ? clamp(chars.depth_m * 2.5, 0.4, 3.0)
        : clamp(0.15 + 1.45 * gN, 0.1, 1.6),
    spacingM: GROUND_SPACING_M,
  };
}
